import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

/*
 * Säilytetään selaimen antama PWA-asennuseventti moduulitasolla.
 *
 * Näin eventti voidaan ottaa talteen heti sovelluksen käynnistyessä
 * ja käyttää myöhemmin esimerkiksi:
 * - etusivun Asenna Paranoidi -napista
 * - Hallinnan käyttäjävalikosta
 */
let deferredInstallPrompt = null;

const installStateListeners = new Set();

function isRunningStandalone() {
  if (typeof window === "undefined") {
    return false;
  }

  const displayModeStandalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches === true;

  const iosStandalone =
    window.navigator?.standalone === true;

  return displayModeStandalone || iosStandalone;
}

function canInstallPwa() {
  return Boolean(deferredInstallPrompt) && !isRunningStandalone();
}

function notifyInstallState() {
  const nextValue = canInstallPwa();

  installStateListeners.forEach((listener) => {
    listener(nextValue);
  });
}

/*
 * beforeinstallprompt tulee vain, jos selain katsoo
 * Paranoidin asennettavaksi.
 *
 * Otetaan eventti talteen ja näytetään oma asennusnappi.
 */
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();

    deferredInstallPrompt = event;
    notifyInstallState();
  });

  /*
   * Jos käyttäjä asentaa Paranoidin selaimen omasta UI:sta
   * tai meidän napistamme, oma asennusnappi poistetaan.
   */
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    notifyInstallState();
  });
}

export default function PwaInstallButton({
  className = "",
  children,
}) {
  const { t } = useTranslation("common");
  const [canInstall, setCanInstall] = useState(() => canInstallPwa());

  useEffect(() => {
    function handleInstallState(nextValue) {
      setCanInstall(nextValue);
    }

    installStateListeners.add(handleInstallState);

    /*
     * Tarkistetaan tila vielä mountissa siltä varalta,
     * että beforeinstallprompt tuli jo ennen komponentin renderöintiä.
     */
    setCanInstall(canInstallPwa());

    return () => {
      installStateListeners.delete(handleInstallState);
    };
  }, []);

  async function handleInstallClick() {
    const promptEvent = deferredInstallPrompt;

    if (!promptEvent) {
      return;
    }

    try {
      /*
       * Tämä avaa selaimen oman PWA-asennusdialogin.
       * prompt() täytyy käynnistää käyttäjän klikkauksesta.
       */
      await promptEvent.prompt();

      await promptEvent.userChoice;
    } catch (error) {
      console.error(
        "Paranoidin asennusdialogin avaaminen epäonnistui:",
        error
      );
    } finally {
      /*
       * Sama BeforeInstallPromptEvent voidaan käyttää vain kerran.
       * Selain antaa myöhemmin uuden eventin, jos asennusta
       * voidaan tarjota uudelleen.
       */
      deferredInstallPrompt = null;
      notifyInstallState();
    }
  }

  if (!canInstall) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleInstallClick}
      className={className}
    >
      {children === undefined ? t("pwa.install") : children}
    </button>
  );
}